/**
 * Resolves the BCP-47 tag handed to the Web Speech API (`SpeechRecognition.lang`).
 *
 * The app's language keys ('es', 'mx', 'vl'…) are not speech-engine tags, and the
 * browser locale must never decide the *language* a student dictates in — only the
 * regional variant of the language the UI is already rendered in.
 */

export const DEFAULT_SPEECH_LOCALE = 'es-ES';

const SPEECH_LOCALE_WHITELIST: readonly string[] = [
  'es-ES',
  'es-MX',
  'es-AR',
  'es-CO',
  'es-CR',
  'es-PA',
  'es-NI',
  'en-US',
  'en-GB',
  'pt-PT',
  'pt-BR',
  'ca-ES',
  'eu-ES',
  'gl-ES',
];

// Bare app language keys: the default region applied when nothing refines them.
const BARE_LANGUAGE_MAP: Record<string, string> = {
  es: 'es-ES',
  en: 'en-US',
  pt: 'pt-PT',
  ca: 'ca-ES',
  eu: 'eu-ES',
  gl: 'gl-ES',
};

// App keys and browser tags that already imply a region but are not valid speech tags.
const REGIONAL_ALIAS_MAP: Record<string, string> = {
  mx: 'es-MX',
  vl: 'ca-ES',
  br: 'pt-BR',
  'es-419': 'es-MX',
};

function canonicalize(raw: string): string {
  const [language, region] = raw.trim().replace('_', '-').split('-');
  const lowerLanguage = language.toLowerCase();
  return region ? `${lowerLanguage}-${region.toUpperCase()}` : lowerLanguage;
}

function baseLanguage(tag: string): string {
  return tag.split('-')[0];
}

/**
 * Normalises a raw app language key or browser locale into a region-qualified
 * speech tag, or `undefined` when the value is not a known language.
 */
export function toSpeechLocale(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;

  const tag = canonicalize(raw);

  if (SPEECH_LOCALE_WHITELIST.includes(tag)) return tag;

  return BARE_LANGUAGE_MAP[tag] ?? REGIONAL_ALIAS_MAP[tag];
}

type ResolveSpeechLocaleParams = {
  browserLanguage: string | null | undefined;
  uiLanguage: string | null | undefined;
};

/**
 * The UI language decides the spoken language. The browser locale may only refine
 * the region, and only when the UI language is bare and both share the same base
 * language ('es' + 'es-MX' → 'es-MX'; 'es' + 'en-US' → 'es-ES').
 */
export function resolveSpeechLocale({ browserLanguage, uiLanguage }: ResolveSpeechLocaleParams): string {
  const uiTag = toSpeechLocale(uiLanguage);
  if (!uiTag) return DEFAULT_SPEECH_LOCALE;

  const isBareUiLanguage = uiLanguage !== undefined && uiLanguage !== null && canonicalize(uiLanguage) in BARE_LANGUAGE_MAP;
  if (!isBareUiLanguage) return uiTag;

  const browserTag = toSpeechLocale(browserLanguage);
  if (browserTag && baseLanguage(browserTag) === baseLanguage(uiTag)) return browserTag;

  return uiTag;
}
